import { useRelatedTracks } from "@/hooks/useRelatedTracks";
import { motion } from "framer-motion";
import {
  Album as AlbumIcon,
  AudioWaveform,
  BarChart2,
  Calendar,
  Clock,
  Disc,
  Edit2,
  FileAudio,
  Grid,
  Heart,
  ListMusic,
  Loader,
  MoreHorizontal,
  Music,
  Play,
  Radio,
  Share2,
  Tag,
  Trash2,
  User,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TrackAnalysis } from "../components/TrackAnalysis";
import { TrackList } from "../components/TrackList";
import { EditTrackDialog } from "../components/TrackList/EditTrackDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { WaveformVisualizer } from "../components/WaveformVisualizer";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { spotifyApi } from "../lib/spotify";
import { TrackAnalysisCache } from "../lib/trackAnalysisCache";
import { useLocalFilesStore } from "../stores/localFilesStore";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { usePlayerStore } from "../stores/playerStore";
import { usePlaylistStore } from "../stores/playlistStore";
import { useStatsStore } from "../stores/statsStore";
import { Track } from "../types";
import { formatTime } from "../utils/formatTime";

export const TrackDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSpotifyAuth();
  const { getAllAlbums, isContentLiked, toggleLike, updateTrackMetadata, getTrack, addLocalTrack } =
    useLocalLibraryStore();
  const { playlists } = usePlaylistStore();
  const { files: localFiles } = useLocalFilesStore();
  const { setCurrentTrack, setIsPlaying, currentTrack, isPlaying } = usePlayerStore();
  const stats = useStatsStore();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const [track, setTrack] = useState<Track | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [occurrences, setOccurrences] = useState(0);
  const [hapticAnalysis, setHapticAnalysis] = useState(null);

  const stableTrack = useMemo(() => track, [track]);
  const { relatedTracks, isLoading: relatedTracksLoading } = useRelatedTracks(stableTrack);

  // Function to initialize demo tracks
  const initializeDemoTracks = useCallback(async () => {
    try {
      const response = await fetch("/demo/tracks.json");
      const data = await response.json();
      data.tracks.forEach((demoTrack: Track) => {
        addLocalTrack(demoTrack);
      });
    } catch (error) {
      console.error("Error initializing demo tracks:", error);
    }
  }, [addLocalTrack]);

  useEffect(() => {
    initializeDemoTracks();
  }, [initializeDemoTracks]);

  const fetchTrack = useCallback(async () => {
    if (!id) return;

    console.log("TrackDetails: Fetching track");
    setIsLoading(true);
    setError(null);
    try {
      const allTracks = [
        ...localFiles,
        ...getAllAlbums().flatMap((album) => album.tracks),
        ...playlists.flatMap((playlist) => playlist.tracks),
      ];
      const localTrack = allTracks.find((track) => track.id === id);
      if (localTrack) {
        console.log("TrackDetails: Local track found", {
          id: localTrack.id,
          title: localTrack.title,
          url: localTrack.url,
          blobDataExists: !!localTrack.blobData,
          blobDataType: localTrack.blobData ? typeof localTrack.blobData : "undefined",
          blobDataLength: localTrack.blobData ? localTrack.blobData.byteLength : 0,
        });
        setTrack(localTrack);
      } else if (isAuthenticated) {
        console.log("TrackDetails: Fetching from Spotify");
        const spotifyTrack = await spotifyApi.tracks.get(id);
        const formattedTrack = {
          id: spotifyTrack.id,
          title: spotifyTrack.name,
          artist: spotifyTrack.artists[0].name,
          albumArt: spotifyTrack.album.images[0]?.url,
          duration: spotifyTrack.duration_ms / 1000,
          url: spotifyTrack.preview_url || "",
          source: "spotify" as const,
          spotifyData: spotifyTrack,
        };
        setTrack(formattedTrack);
      } else {
        // Check for demo tracks
        const response = await fetch("/demo/tracks.json");
        const data = await response.json();
        const demoTrack = data.tracks.find((t: Track) => t.id === id);
        if (demoTrack) {
          setTrack(demoTrack);
        } else {
          throw new Error("Track not found");
        }
      }

      const trackOccurrences = allTracks.filter((t) => t.id === id).length;
      setOccurrences(trackOccurrences);
    } catch (err) {
      console.error("TrackDetails: Error fetching track", err);
      setError(err instanceof Error ? err : new Error("An unknown error occurred"));
    } finally {
      setIsLoading(false);
    }
  }, [id, localFiles, getAllAlbums, playlists, isAuthenticated]);

  useEffect(() => {
    fetchTrack();
  }, [fetchTrack]);

  useEffect(() => {
    const loadHapticAnalysis = async () => {
      if (!track?.id) return;
      try {
        const analysis = await TrackAnalysisCache.getAnalysis(track.id);
        setHapticAnalysis(analysis);
      } catch (error) {
        console.error("Failed to load haptic analysis:", error);
      }
    };

    loadHapticAnalysis();
  }, [track?.id]);

  const handleTrackUpdate = useCallback(
    (updatedTrack: Partial<Track>) => {
      console.log("handleTrackUpdate called with:", updatedTrack);
      if (track) {
        try {
          // Clean up the update data
          const cleanUpdate = {
            ...updatedTrack,
            albumArt: updatedTrack.albumArt || null, // Convert empty string to null
          };

          // Update in store
          updateTrackMetadata(track.id, cleanUpdate);

          // Update local state
          setTrack((prevTrack) => {
            if (prevTrack) {
              const newTrack = { ...prevTrack, ...cleanUpdate };
              console.log("Updated track:", newTrack);
              return newTrack;
            }
            return prevTrack;
          });

          setShowEditDialog(false);
        } catch (error) {
          console.error("Error updating track:", error);
        }
      } else {
        console.error("Cannot update track: track is null");
      }
    },
    [track, updateTrackMetadata]
  );

  const handlePlay = async () => {
    if (!track) return;

    console.log("Playing track:", {
      id: track.id,
      title: track.title,
      artist: track.artist,
      source: track.source,
      url: track.url,
      blobDataExists: !!track.blobData,
      blobDataType: track.blobData ? track.blobData.constructor.name : "N/A",
      blobDataLength: track.blobData instanceof ArrayBuffer ? track.blobData.byteLength : "N/A",
    });

    let updatedTrack = track;

    // If it's a local track, always try to fetch the latest data
    if (track.source === "local") {
      const fetchedTrack = await getTrack(track.id);
      if (fetchedTrack) {
        updatedTrack = fetchedTrack;
      }
    }

    if (currentTrack?.id === updatedTrack.id) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentTrack(updatedTrack);
      setIsPlaying(true);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: track?.title,
        text: `Check out this track: ${track?.title} by ${track?.artist}`,
        url: window.location.href,
      });
    } catch (error) {
      console.log("Sharing failed", error);
    }
  };

  const handleDelete = () => {
    // Handle track deletion
    navigate("/library");
  };

  // Get track stats
  const playCount = stats.playHistory.filter((event) => event.trackId === track?.id).length;
  const lastPlayed = stats.playHistory
    .filter((event) => event.trackId === track?.id)
    .sort((a, b) => b.timestamp - a.timestamp)[0]?.timestamp;

  const isLiked = track ? isContentLiked(track.id, "track") : false;
  const source = track?.source || "local";

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <Loader className="w-8 h-8 animate-spin text-yellow-500" />
          <p className="text-zinc-400">Loading track details...</p>
        </motion.div>
      </div>
    );
  }

  if (error || !track) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-8">
        <Music className="w-16 h-16 text-zinc-600 mb-4" />
        <h1 className="text-2xl font-bold mb-4">Track not found</h1>
        <p className="text-zinc-400 mb-6">This track might have been deleted or is not available.</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-800/50 to-zinc-900">
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        {/* Track Header */}
        <div className="flex flex-col md:flex-row justify-between mb-8">
          <div className="md:text-left">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{track.title}</h1>
            <div className="flex flex-wrap justify-start gap-4 gap-y-2 text-zinc-400">
              <div className="flex items-center gap-2">
                <Disc className="w-5 h-5" />
                <span>{track.artist}</span>
              </div>
              {(track.spotifyData?.album || track.source === "local") && (
                <a
                  href={
                    track.spotifyData?.album
                      ? `/albums/${track.spotifyData.album.id}`
                      : `/albums/${track.album}` || `/albums/demo-album-1`
                  }
                  className="flex items-center gap-2 hover:text-white transition"
                >
                  <AlbumIcon className="w-5 h-5" />
                  <span>{track.spotifyData?.album?.name || track.album}</span>
                </a>
              )}
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{formatTime(track.duration)}</span>
              </div>
              <div className="flex items-center gap-2">
                <BarChart2 className="w-5 h-5" />
                <span>{playCount} plays</span>
              </div>
              <div className="flex items-center gap-2">
                <ListMusic className="w-5 h-5" />
                <span>
                  In {occurrences} collection{occurrences !== 1 ? "s" : ""}
                </span>
              </div>
              {lastPlayed && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>Last played {new Date(lastPlayed).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 md:mt-0">
            <button
              onClick={handlePlay}
              className="w-full min-w-32 md:w-auto px-4 py-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition flex items-center justify-center gap-2"
            >
              {currentTrack?.id === track.id && isPlaying ? (
                <>
                  <div className="w-4 h-4 relative">
                    <div className="absolute inset-0 bg-white rounded-full animate-ping opacity-75"></div>
                    <div className="relative w-4 h-4 bg-white rounded-full"></div>
                  </div>
                  <span className="hidden md:inline">Playing</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span className="hidden md:inline">Play</span>
                </>
              )}
            </button>
            <button
              onClick={() => toggleLike(track.id, "track", source)}
              className={`p-2 md:p-3 transition ${isLiked ? "text-yellow-500" : "text-zinc-400 hover:text-white"}`}
            >
              <Heart className="w-5 h-5" fill={isLiked ? "currentColor" : "none"} />
            </button>
            <button onClick={handleShare} className="p-2 md:p-3 text-zinc-400 hover:text-white transition">
              <Share2 className="w-5 h-5" />
            </button>
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 md:p-3 text-zinc-400 hover:text-white transition"
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>

              {showMenu && (
                <div className="absolute right-0 mt-2 w-40 md:w-48 bg-zinc-800 rounded-md shadow-lg z-50">
                  {source === "local" && (
                    <>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setShowEditDialog(true);
                        }}
                        className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-zinc-700"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit Track
                      </button>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setShowDeleteConfirm(true);
                        }}
                        className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-zinc-700 text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Track
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Track List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-zinc-800/50 rounded-lg overflow-hidden"
        >
          <TrackList
            tracks={[track]}
            showAlbum={false}
            showHeader={true}
            image={track.albumArt}
            title={track.title}
            subtitle={track.artist}
          />
        </motion.div>

        <Tabs defaultValue="info" className="mt-8">
          <TabsList className="w-full">
            <TabsTrigger value="info" className="flex-1">
              Song Info & Artist
            </TabsTrigger>
            <TabsTrigger value="details" className="flex-1">
              Analysis & Stats
            </TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-6">
            {/* Related Songs & Artist Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Related Songs Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-800/50 rounded-lg p-6"
              >
                <h2 className="text-xl font-bold mb-4">Related Songs</h2>
                <div className="space-y-4">
                  {relatedTracksLoading ? (
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Loading related songs...</span>
                    </div>
                  ) : relatedTracks.length > 0 ? (
                    <TrackList
                      tracks={relatedTracks}
                      showHeader={false}
                      compact={true}
                      image={relatedTracks[0]?.albumArt}
                      title="Related Songs"
                      subtitle={track.artist}
                    />
                  ) : (
                    <div className="text-zinc-400">No related songs found</div>
                  )}
                </div>
              </motion.div>

              {/* Artist Information */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-800/50 rounded-lg p-6"
              >
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-yellow-500" />
                  Artist Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Popular Tracks</h3>
                    <div className="space-y-2">
                      {/* Placeholder for popular tracks */}
                      <div className="text-zinc-400">No data available</div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Related Artists</h3>
                    <div className="space-y-2">
                      {/* Placeholder for related artists */}
                      <div className="text-zinc-400">No data available</div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Artist Stats</h3>
                    <div className="space-y-2">
                      <div className="flex flex-col items-left justify-between">
                        <span className="text-zinc-400">Your Total Plays</span>
                        <span>{stats.playHistory.filter((e) => e.trackData.artist === track.artist).length}</span>
                      </div>
                      <div className="flex flex-col items-left justify-between">
                        <span className="text-zinc-400">First Played</span>
                        <span>
                          {new Date(
                            stats.playHistory.find((e) => e.trackData.artist === track.artist)?.timestamp || Date.now()
                          ).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </TabsContent>

          <TabsContent value="details" className="space-y-6">
            {/* Analysis & Stats */}
            <div className="grid grid-cols-1 gap-6">
              {/* Audio Analysis */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-800/50 rounded-lg p-6"
              >
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <AudioWaveform className="w-5 h-5 text-yellow-500" />
                  Audio Waveform & Analysis
                </h2>
                {/* Waveform Visualizer */}
                <div className="mb-6">
                  <WaveformVisualizer
                    source={track.source === "local" ? track.blobData || track.url : track.url}
                    height={80}
                    onError={(error) => console.error("Waveform error:", error)}
                  />
                </div>

                {/* Analysis Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex flex-col gap-2">
                    <div className="text-zinc-400 text-sm">BPM</div>
                    <div className="text-lg font-medium">
                      {track.spotifyData?.audio_features?.tempo?.toFixed(0) || "N/A"}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-zinc-400 text-sm">Key</div>
                    <div className="text-lg font-medium">{track.spotifyData?.audio_features?.key || "N/A"}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-zinc-400 text-sm">Energy</div>
                    <div className="w-full bg-zinc-700 rounded-full h-2">
                      <div
                        className="bg-yellow-500 h-2 rounded-full"
                        style={{ width: `${(track.spotifyData?.audio_features?.energy || 0) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-zinc-400 text-sm">Danceability</div>
                    <div className="w-full bg-zinc-700 rounded-full h-2">
                      <div
                        className="bg-yellow-500 h-2 rounded-full"
                        style={{ width: `${(track.spotifyData?.audio_features?.danceability || 0) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Track Metadata */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-800/50 rounded-lg p-6"
              >
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <FileAudio className="w-5 h-5 text-yellow-500" />
                  Track Metadata
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Tag className="w-4 h-4" />
                    <span>Genre: {track.spotifyData?.genres?.join(", ") || "Unknown"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Radio className="w-4 h-4" />
                    <span>Format: {track.source === "spotify" ? "Streaming" : "Local File"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Grid className="w-4 h-4" />
                    <span>Quality: {track.source === "spotify" ? "320kbps" : "Variable"}</span>
                  </div>
                </div>
              </motion.div>

              {/* Track Statistics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-800/50 rounded-lg p-6"
              >
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Extended Statistics
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="flex flex-col gap-2">
                    <div className="text-zinc-400 text-sm">Skip Rate</div>
                    <div className="text-lg font-medium">
                      {(
                        (stats.playHistory.filter((e) => e.trackId === track.id && e.skipped).length /
                          Math.max(stats.playHistory.filter((e) => e.trackId === track.id).length, 1)) *
                        100
                      ).toFixed(1)}
                      %
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-zinc-400 text-sm">Avg. Listen Time</div>
                    <div className="text-lg font-medium">
                      {formatTime(
                        stats.playHistory
                          .filter((e) => e.trackId === track.id)
                          .reduce((acc, e) => acc + e.duration, 0) /
                          Math.max(stats.playHistory.filter((e) => e.trackId === track.id).length, 1)
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-zinc-400 text-sm">First Played</div>
                    <div className="text-lg font-medium">
                      {new Date(
                        stats.playHistory.find((e) => e.trackId === track.id)?.timestamp || Date.now()
                      ).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-zinc-400 text-sm">Play Streak</div>
                    <div className="text-lg font-medium">
                      {stats.playHistory
                        .filter((e) => e.trackId === track.id)
                        .reduce((acc, curr, i, arr) => {
                          if (i === 0) return 1;
                          const diff = curr.timestamp - arr[i - 1].timestamp;
                          return diff < 86400000 ? acc + 1 : acc;
                        }, 0)}{" "}
                      days
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Add Haptic Analysis */}
        <div className="mt-8">
          <TrackAnalysis hapticAnalysis={hapticAnalysis} />
        </div>
      </div>

      {/* Dialogs */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Track"
        message={`Are you sure you want to delete "${track.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        type="danger"
      />

      {showEditDialog && source === "local" && (
        <EditTrackDialog
          isOpen={true}
          onClose={() => setShowEditDialog(false)}
          track={track}
          onSave={handleTrackUpdate}
        />
      )}
    </div>
  );
};
